/**
 * React Query hooks for Portfolio operations.
 *
 * T031 [US1]: Implement React Query hooks for position CRUD operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPositions,
  getPosition,
  createPosition,
  updatePosition,
  deletePosition,
  getPortfolioTotal,
  type Position,
  type PortfolioTotal,
} from '../../../services/portfolioApi';

/**
 * Hook to fetch all positions for the current user.
 * Auto-refetches when screen comes into focus.
 */
export function usePositions() {
  return useQuery<Position[]>({
    queryKey: ['positions'],
    queryFn: getPositions,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

/**
 * Hook to fetch a single position by ID.
 *
 * @param positionId - Position ID to fetch
 * @param enabled - Whether to enable query (default: true)
 */
export function usePosition(positionId: number, enabled: boolean = true) {
  return useQuery<Position>({
    queryKey: ['position', positionId],
    queryFn: () => getPosition(positionId),
    enabled: enabled && !!positionId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to create a new position.
 * Automatically invalidates the positions cache on success.
 */
export function useCreatePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      symbol: string;
      quantity: number;
      cost_price: number;
      currency: string;
      merge_with_existing?: boolean;
      asset_type?: string;
      sector?: string;
      industry?: string;
    }) => {
      return await createPosition(params);
    },
    onSuccess: () => {
      // Invalidate and refetch positions
      queryClient.invalidateQueries({ queryKey: ['positions'] });
      // Allocation % and base-currency totals derive from the portfolio total
      // (PositionTable derives per-currency FX from it) — refresh it.
      queryClient.invalidateQueries({ queryKey: ['portfolioTotal'] });
    },
    onError: (error: Error) => {
      console.error('Failed to create position:', error);
      throw error; // Re-throw to let component handle UI feedback
    },
  });
}

/**
 * Hook to update an existing position.
 * Automatically invalidates the positions cache on success.
 */
export function useUpdatePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      positionId,
      params,
    }: {
      positionId: number;
      params: {
        quantity?: number;
        cost_price?: number;
        close_price?: number | null;
        currency?: string;
        asset_type?: string;
        sector?: string;
        industry?: string;
      };
    }) => {
      return await updatePosition(positionId, params);
    },
    onSuccess: (_, variables) => {
      // Invalidate both the list and the specific position
      queryClient.invalidateQueries({ queryKey: ['positions'] });
      queryClient.invalidateQueries({ queryKey: ['position', variables.positionId] });
      // Refresh the portfolio total so allocation/FX isn't stale after the change.
      queryClient.invalidateQueries({ queryKey: ['portfolioTotal'] });
    },
    onError: (error: Error) => {
      console.error('Failed to update position:', error);
      throw error;
    },
  });
}

/**
 * Hook to delete a position.
 * Automatically invalidates the positions cache on success.
 */
export function useDeletePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (positionId: number) => {
      await deletePosition(positionId);
    },
    onSuccess: () => {
      // Invalidate and refetch positions
      queryClient.invalidateQueries({ queryKey: ['positions'] });
      // Allocation % and base-currency totals derive from the portfolio total
      // (PositionTable derives per-currency FX from it) — refresh it.
      queryClient.invalidateQueries({ queryKey: ['portfolioTotal'] });
    },
    onError: (error: Error) => {
      console.error('Failed to delete position:', error);
      throw error;
    },
  });
}

/**
 * Hook to fetch portfolio total with currency breakdown.
 */
export function usePortfolioTotal() {
  return useQuery<PortfolioTotal>({
    queryKey: ['portfolioTotal'],
    queryFn: getPortfolioTotal,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}
